<div class="test-upload-container">
  <p-toast></p-toast>

  <div class="header">
    <h1>Test de Données avec Upload</h1>
    <p class="subtitle">
      Uploadez un fichier CSV ou Excel pour tester et valider vos données selon le formulaire
    </p>
  </div>

  <p-card class="upload-card">
    <form [formGroup]="uploadForm" (ngSubmit)="onSubmit()">
      <div class="mode-selector">
        <button
          pButton
          type="button"
          [label]="processingMode === 'export' ? '📥 Export Nettoyé (Recommandé)' : processingMode === 'ai' ? '🤖 IA + Comparaisons' : '📋 Manuel'"
          [icon]="processingMode === 'export' ? 'pi pi-download' : processingMode === 'ai' ? 'pi pi-bolt' : 'pi pi-cog'"
          (click)="toggleProcessingMode()"
          [class]="processingMode === 'export' ? 'p-button-info' : processingMode === 'ai' ? 'p-button-success' : 'p-button-secondary'"
          class="p-button-sm">
        </button>
        <span class="mode-description">
          {{ processingMode === 'export' ? 'Génère un fichier Excel/CSV propre conforme à vos énumérations' : 
             processingMode === 'ai' ? 'L\'IA nettoie et compare automatiquement (526 lignes)' : 
             'Traitement manuel ligne par ligne' }}
        </span>
        
        <!-- Format selector pour mode export -->
        <button
          *ngIf="processingMode === 'export'"
          pButton
          type="button"
          [label]="exportFormat === 'excel' ? '📊 Excel' : '📄 CSV'"
          (click)="toggleExportFormat()"
          class="p-button-sm p-button-outlined">
        </button>
      </div>

      <div class="upload-section">
        <p-fileUpload
          name="file"
          mode="basic"
          [chooseLabel]="uploadedFile ? 'Changer le fichier' : 'Choisir un fichier'"
          accept=".csv,.xlsx,.xls"
          [maxFileSize]="10000000"
          [auto]="false"
          [showUploadButton]="false"
          [showCancelButton]="false"
          (onSelect)="onFileSelect($event)"
          (onClear)="onFileRemove()"
          [customUpload]="true">
        </p-fileUpload>

        <div class="file-info" *ngIf="uploadedFile">
          <i class="pi pi-file"></i>
          <span class="file-name">{{ uploadedFile.name }}</span>
          <span class="file-size">({{ (uploadedFile.size / 1024).toFixed(2) }} KB)</span>
          <button
            pButton
            type="button"
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-danger"
            (click)="onFileRemove()">
          </button>
        </div>
      </div>

      <div class="action-buttons">
        <button
          pButton
          type="submit"
          [label]="processingMode === 'export' ? '📥 Générer fichier propre' : processingMode === 'ai' ? '🚀 Lancer avec IA' : 'Lancer le test'"
          icon="pi pi-play"
          [disabled]="!uploadedFile || isLoading || isComparingData"
          [loading]="isLoading"
          class="p-button-primary">
        </button>

        <button
          pButton
          type="button"
          label="Réinitialiser"
          icon="pi pi-refresh"
          (click)="reset()"
          [disabled]="isLoading || isComparingData"
          class="p-button-secondary">
        </button>
      </div>
    </form>
  </p-card>

  <!-- Barre de progression pour les comparaisons -->
  <p-card class="progress-card" *ngIf="isComparingData">
    <div class="progress-content">
      <h3>Comparaisons en cours...</h3>
      <p-progressBar [value]="comparisonProgress" [showValue]="true"></p-progressBar>
      <p class="progress-text">{{ comparisonProgress }}% - Traitement de {{ aiProcessedData.length }} lignes</p>
    </div>
  </p-card>

  <div class="results-section" *ngIf="showResults && testResults">
    <p-card class="results-card">
      <ng-template pTemplate="header">
        <div class="results-header">
          <h2>Résultats {{ processingMode === 'ai' ? '(Traité par IA)' : '' }}</h2>
          <button
            pButton
            type="button"
            label="Télécharger les données nettoyées"
            icon="pi pi-download"
            (click)="downloadCleanedData()"
            class="p-button-sm"
            *ngIf="testResults.cleaned_data">
          </button>
        </div>
      </ng-template>

      <div class="summary">
        <div class="summary-card">
          <div class="summary-icon total">
            <i class="pi pi-list"></i>
          </div>
          <div class="summary-content">
            <span class="summary-label">Total de lignes</span>
            <span class="summary-value">{{ testResults.total_rows }}</span>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon valid">
            <i class="pi pi-check-circle"></i>
          </div>
          <div class="summary-content">
            <span class="summary-label">Lignes valides</span>
            <span class="summary-value">{{ testResults.valid_rows }}</span>
          </div>
        </div>

        <div class="summary-card">
          <div class="summary-icon invalid">
            <i class="pi pi-times-circle"></i>
          </div>
          <div class="summary-content">
            <span class="summary-label">Lignes invalides</span>
            <span class="summary-value">{{ testResults.invalid_rows }}</span>
          </div>
        </div>
      </div>

      <div class="results-table">
        <p-table
          [value]="testResults.results"
          [paginator]="true"
          [rows]="10"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} lignes"
          [rowsPerPageOptions]="[10, 25, 50]"
          styleClass="p-datatable-striped">
          
          <ng-template pTemplate="header">
            <tr>
              <th>Ligne</th>
              <th>Statut</th>
              <th>Données</th>
              <th>Erreurs/Avertissements</th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-result>
            <tr [ngClass]="getStatusClass(result.status)">
              <td>{{ result.row_number }}</td>
              <td>
                <span class="status-badge" [ngClass]="result.status">
                  {{ result.status === 'valid' ? 'Valide' : 
                     result.status === 'invalid' ? 'Invalide' : 
                     'Avertissement' }}
                </span>
              </td>
              <td>
                <div class="data-preview">
                  <pre>{{ result.data | json }}</pre>
                </div>
              </td>
              <td>
                <div class="messages" *ngIf="result.errors && result.errors.length > 0">
                  <div class="error-message" *ngFor="let error of result.errors">
                    <i class="pi pi-exclamation-triangle"></i>
                    {{ error }}
                  </div>
                </div>
                <div class="messages" *ngIf="result.warnings && result.warnings.length > 0">
                  <div class="warning-message" *ngFor="let warning of result.warnings">
                    <i class="pi pi-info-circle"></i>
                    {{ warning }}
                  </div>
                </div>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center">Aucun résultat trouvé</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </p-card>
  </div>

  <div class="loading-overlay" *ngIf="isLoading">
    <p-progressSpinner></p-progressSpinner>
    <p>Traitement en cours...</p>
  </div>
</div>
